import { useCallback, useMemo, useRef } from "react";
import type { SchedulerEvent } from "../hooks/useScheduler";
import {
  formatDateKey,
  formatDayHeader,
  slotToTime,
} from "../hooks/useScheduler";
import type { Marker } from "../api/schedulerApi";

/* ── Constants ────────────────────────────────────────────────────── */

const TOTAL_SLOTS = 96; // 24h × 4 (15-min resolution)

/** Event with layout metadata for side-by-side overlap rendering. */
type PositionedEvent = SchedulerEvent & { _col: number; _cols: number };

/**
 * Assign a column index to every overlapping event in a day so that
 * concurrent events render side-by-side instead of hiding behind each other.
 *
 * Algorithm:
 *   1. Sort by start_slot (earliest first); tie-break by end_slot descending
 *      so longer events anchor the left column.
 *   2. Greedy column assignment — each event takes the lowest free column
 *      not used by a currently-overlapping event.
 *   3. Per event, columns = max simultaneous overlaps across the slots the
 *      event spans. All events in the same cluster share the same `cols`
 *      value so widths align.
 */
function layoutDay(events: SchedulerEvent[]): PositionedEvent[] {
  if (!events.length) return [];

  // Active per-slot: count of overlapping events (including `ev` itself).
  const slotCount: number[] = new Array(TOTAL_SLOTS).fill(0);
  for (const ev of events) {
    for (let s = ev.start_slot; s < ev.end_slot && s < TOTAL_SLOTS; s++) {
      slotCount[s]++;
    }
  }

  const sorted = [...events].sort(
    (a, b) =>
      a.start_slot - b.start_slot ||
      b.end_slot - a.end_slot ||
      String(a.id).localeCompare(String(b.id)),
  );

  const placed: PositionedEvent[] = [];

  for (const ev of sorted) {
    // Occupied columns among currently-overlapping already-placed events.
    const taken = new Set<number>();
    for (const p of placed) {
      if (p.start_slot < ev.end_slot && p.end_slot > ev.start_slot) {
        taken.add(p._col);
      }
    }
    let col = 0;
    while (taken.has(col)) col++;

    // Cluster width = max simultaneous overlaps across any slot this event spans.
    let maxOverlap = 0;
    for (let s = ev.start_slot; s < ev.end_slot && s < TOTAL_SLOTS; s++) {
      if (slotCount[s] > maxOverlap) maxOverlap = slotCount[s];
    }

    placed.push({ ...ev, _col: col, _cols: Math.max(1, maxOverlap) });
  }

  return placed;
}

interface Props {
  weekDays: Date[];
  events: SchedulerEvent[];
  markers?: Marker[];
  locked: boolean;
  dragRange: { date: string; startSlot: number; endSlot: number } | null;
  eventsForDay: (dateKey: string) => SchedulerEvent[];
  onDragStart: (date: string, slot: number) => void;
  onDragMove: (date: string, slot: number) => void;
  onDragEnd: () => void;
  onDragCancel: () => void;
  onClickEvent: (event: SchedulerEvent) => void;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Convert ISO timestamp to the slot index within a day. */
function timestampToSlot(ts: string): number {
  const d = new Date(ts);
  return d.getHours() * 4 + Math.floor(d.getMinutes() / 15);
}

export default function SchedulerGrid({
  weekDays,
  markers = [],
  locked,
  dragRange,
  eventsForDay,
  onDragStart,
  onDragMove,
  onDragEnd,
  onDragCancel,
  onClickEvent,
}: Props) {
  const isDragging = useRef(false);
  const today = new Date();

  // Precompute side-by-side layout once per day. Each event carries
  // `_col`/`_cols` so overlapping events render in parallel columns
  // instead of stacking invisibly.
  const layoutByDay = useMemo(() => {
    const map = new Map<string, PositionedEvent[]>();
    for (const day of weekDays) {
      const dateKey = formatDateKey(day);
      map.set(dateKey, layoutDay(eventsForDay(dateKey)));
    }
    return map;
  }, [weekDays, eventsForDay]);

  const handleMouseDown = useCallback(
    (dateKey: string, slot: number) => {
      if (locked) return;
      isDragging.current = true;
      onDragStart(dateKey, slot);
    },
    [locked, onDragStart],
  );

  const handleMouseEnter = useCallback(
    (dateKey: string, slot: number) => {
      if (!isDragging.current) return;
      onDragMove(dateKey, slot);
    },
    [onDragMove],
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    onDragEnd();
  }, [onDragEnd]);

  const handleMouseLeave = useCallback(() => {
    if (isDragging.current) {
      isDragging.current = false;
      onDragCancel();
    }
  }, [onDragCancel]);

  /** Get markers for a specific day column. */
  function markersForDay(dateKey: string): Marker[] {
    return markers.filter((m) => m.timestamp.startsWith(dateKey));
  }

  return (
    <div
      className="sch-grid-wrapper"
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {/* ── Column headers (days) ──────────────────────────────── */}
      <div className="sch-header-row">
        <div className="sch-time-gutter sch-header-cell" />
        {weekDays.map((day) => {
          const key = formatDateKey(day);
          const isToday = isSameDay(day, today);
          return (
            <div
              key={key}
              className={`sch-header-cell${isToday ? " sch-header-cell--today" : ""}`}
            >
              {formatDayHeader(day)}
            </div>
          );
        })}
      </div>

      {/* ── Scrollable time body ───────────────────────────────── */}
      <div className="sch-body">
        {/* ── Marker vertical lines (absolute within body) ──── */}
        {weekDays.map((day, dayIdx) => {
          const dateKey = formatDateKey(day);
          const dayMarkers = markersForDay(dateKey);
          return dayMarkers.map((marker) => {
            const slot = timestampToSlot(marker.timestamp);
            const topPct = (slot / TOTAL_SLOTS) * 100;
            return (
              <div
                key={marker.id}
                className="sch-marker"
                style={{
                  left: `calc(56px + ${(dayIdx / 7) * 100}% * (1 - 56px / 100%))`,
                  top: `${topPct}%`,
                  borderColor: marker.color,
                }}
                title={`${marker.label} (${marker.marker_type})`}
              >
                <span
                  className="sch-marker-badge"
                  style={{ background: marker.color }}
                >
                  {marker.label}
                </span>
              </div>
            );
          });
        })}

        {Array.from({ length: TOTAL_SLOTS }, (_, slot) => {
          // Determine grid line type: hour | half | quarter
          const lineType =
            slot % 4 === 0
              ? "sch-cell--hour"
              : slot % 2 === 0
                ? "sch-cell--half"
                : "sch-cell--quarter";

          return (
            <div key={slot} className="sch-row">
              {/* Time gutter — show label on the hour */}
              <div className="sch-time-gutter">
                {slot % 4 === 0 ? (
                  <span className="sch-time-label">{slotToTime(slot)}</span>
                ) : null}
              </div>

              {/* Day columns */}
              {weekDays.map((day) => {
                const dateKey = formatDateKey(day);
                const isDragTarget =
                  dragRange &&
                  dragRange.date === dateKey &&
                  slot >= dragRange.startSlot &&
                  slot <= dragRange.endSlot;

                const dayEvents = layoutByDay.get(dateKey) ?? [];
                // Is any event active in this slot? (for drag-blocking)
                const hasAnyEvent = dayEvents.some(
                  (ev) => slot >= ev.start_slot && slot < ev.end_slot,
                );
                // Events starting at this slot — we render each one here,
                // positioned side-by-side when they share the cluster.
                const eventsStartingHere = dayEvents.filter(
                  (ev) => ev.start_slot === slot,
                );

                return (
                  <div
                    key={dateKey}
                    className={[
                      "sch-cell",
                      lineType,
                      isDragTarget ? "sch-cell--drag" : "",
                      locked ? "sch-cell--locked" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onMouseDown={(e) => {
                      if (hasAnyEvent) return;
                      e.preventDefault();
                      handleMouseDown(dateKey, slot);
                    }}
                    onMouseEnter={() => handleMouseEnter(dateKey, slot)}
                  >
                    {eventsStartingHere.map((ev) => {
                      // Side-by-side positioning: use percentage left/width
                      // so overlapping events are all visible with their
                      // individual colors. Margins handled via calc()
                      // so adjacent events don't touch.
                      const widthPct = 100 / ev._cols;
                      const leftPct = ev._col * widthPct;
                      return (
                        <button
                          key={ev.id}
                          type="button"
                          className="sch-event"
                          style={{
                            height: `${(ev.end_slot - ev.start_slot) * 100}%`,
                            background: ev.color,
                            borderColor: ev.color,
                            left: `calc(${leftPct}% + 2px)`,
                            width: `calc(${widthPct}% - 3px)`,
                            right: "auto",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onClickEvent(ev);
                          }}
                          title={`${ev.title || "(untitled)"}  ·  ${ev.start_time}–${ev.end_time}`}
                        >
                          <span className="sch-event__time">
                            {ev.start_time}–{ev.end_time}
                          </span>
                          <span className="sch-event__title">
                            {ev.recurring && ev.recurring !== "none" && (
                              <span className="sch-event__recurring" title={`Recurring: ${ev.recurring}`}>&#x1F501;</span>
                            )}
                            {ev.title || "(untitled)"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
